import { AnalyticsService } from "@/lib/analytics/analytics.service";

export const IN_APP_BROWSER_AUTH_CODE = "auth/in-app-browser";

const NAMED_IN_APP_BROWSERS: Array<{ pattern: RegExp; name: string }> = [
	{ pattern: /LinkedInApp/i, name: "LinkedIn" },
	{ pattern: /Instagram/i, name: "Instagram" },
	{ pattern: /FBAN|FBAV|FB_IAB|FB4A|FBIOS/i, name: "Facebook" },
	{ pattern: /TikTok|musical_ly|BytedanceWebview/i, name: "TikTok" },
	{ pattern: /Snapchat/i, name: "Snapchat" },
	{ pattern: /Twitter|TwitterAndroid/i, name: "X" },
	{ pattern: /Line\//i, name: "LINE" },
	{ pattern: /MicroMessenger|WeChat/i, name: "WeChat" },
	{ pattern: /WhatsApp/i, name: "WhatsApp" },
	{ pattern: /Pinterest/i, name: "Pinterest" },
	{ pattern: /Reddit/i, name: "Reddit" },
	{ pattern: /Telegram/i, name: "Telegram" },
];

export type InAppBrowserInfo = {
	isInAppBrowser: boolean;
	appName: string | null;
	isIOS: boolean;
	isAndroid: boolean;
};

export function detectInAppBrowser(userAgent: string): InAppBrowserInfo {
	const isIOS = /iPad|iPhone|iPod/.test(userAgent);
	const isAndroid = /Android/i.test(userAgent);

	if (/Capacitor/i.test(userAgent)) {
		return { isInAppBrowser: false, appName: null, isIOS, isAndroid };
	}

	for (const { pattern, name } of NAMED_IN_APP_BROWSERS) {
		if (pattern.test(userAgent)) {
			return { isInAppBrowser: true, appName: name, isIOS, isAndroid };
		}
	}

	// iOS WKWebView used by in-app browsers usually has AppleWebKit + Mobile
	// but no Safari / Chrome / Firefox token.
	if (
		isIOS &&
		/AppleWebKit/i.test(userAgent) &&
		!/Safari|CriOS|FxiOS|EdgiOS|Chrome|Firefox/i.test(userAgent)
	) {
		return { isInAppBrowser: true, appName: null, isIOS, isAndroid };
	}

	if (isAndroid && /; wv\)/i.test(userAgent)) {
		return { isInAppBrowser: true, appName: null, isIOS, isAndroid };
	}

	return { isInAppBrowser: false, appName: null, isIOS, isAndroid };
}

export function getInAppBrowserInfo(): InAppBrowserInfo {
	if (typeof navigator === "undefined") {
		return {
			isInAppBrowser: false,
			appName: null,
			isIOS: false,
			isAndroid: false,
		};
	}
	return detectInAppBrowser(navigator.userAgent);
}

export function isInAppBrowser(): boolean {
	return getInAppBrowserInfo().isInAppBrowser;
}

export function isInAppBrowserAuthError(error: unknown): boolean {
	return Boolean(
		error &&
			typeof error === "object" &&
			"code" in error &&
			(error as { code?: string }).code === IN_APP_BROWSER_AUTH_CODE,
	);
}

export function createInAppBrowserAuthError(): Error & { code: string } {
	const error = new Error(IN_APP_BROWSER_AUTH_CODE) as Error & {
		code: string;
	};
	error.code = IN_APP_BROWSER_AUTH_CODE;
	return error;
}

/**
 * Try to leave the in-app webview. iOS uses the unofficial x-safari scheme;
 * Android uses an https intent. Both can fail, so callers should also show
 * a manual “open in Safari/Chrome” hint.
 */
export function openInExternalBrowser(
	url: string = typeof window === "undefined" ? "" : window.location.href,
	reason: "banner" | "oauth" = "banner",
): void {
	if (typeof window === "undefined" || !url) {
		return;
	}

	const info = getInAppBrowserInfo();
	AnalyticsService.track(
		reason === "oauth"
			? "in_app_browser_oauth_blocked"
			: "in_app_browser_open_clicked",
		{
			app: info.appName,
			os: info.isIOS ? "ios" : info.isAndroid ? "android" : "other",
		},
	);

	if (info.isIOS) {
		const safariUrl = url
			.replace(/^https:\/\//, "x-safari-https://")
			.replace(/^http:\/\//, "x-safari-http://");
		window.location.href = safariUrl;
		return;
	}

	if (info.isAndroid) {
		const withoutScheme = url.replace(/^https?:\/\//, "");
		window.location.href = `intent://${withoutScheme}#Intent;scheme=https;action=android.intent.action.VIEW;end`;
		return;
	}

	window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * Call before starting a popup/redirect OAuth flow. Opens the system browser
 * and throws a silent auth error so callers can abort without a toast.
 */
export function blockOAuthInInAppBrowser(): void {
	if (!isInAppBrowser()) {
		return;
	}
	openInExternalBrowser(
		typeof window === "undefined" ? "" : window.location.href,
		"oauth",
	);
	throw createInAppBrowserAuthError();
}
